import { config } from '@/lib/config';

import {
  normalizeSetupCountryCode,
  resolveSetupCountryFromLocale,
  resolveSetupPreviewInitParams,
  type SetupPreviewInitContext,
} from '@/components/setup/experience/lib/setupCheckoutCountry';

export type {
  SetupPreviewInitContext,
} from '@/components/setup/experience/lib/setupCheckoutCountry';

export {
  normalizeSetupCountryCode,
  resolveSetupCheckoutLocale,
  resolveSetupCountryFromLocale,
  resolveSetupPreviewInitParams,
  resolveSetupShippingCountryCode,
} from '@/components/setup/experience/lib/setupCheckoutCountry';

export interface SetupCheckoutInitLineItem {
  variantId: string;
  priceId: string;
  quantity: number;
}

export interface BuildCheckoutInitUrlParams {
  storeId: string;
  funnelId: string;
  currency: string;
  checkoutUrl?: string;
  lineItems?: SetupCheckoutInitLineItem[];
  /** BCP-47 locale (e.g. fr-FR) — drives checkout copy */
  locale?: string;
  /** ISO alpha-2 country for shipping rate lookup at init (e.g. FR) */
  shippingCountryCode?: string;
  /** Setup preview uses staging funnel URLs */
  draft?: boolean;
  includeCheckoutToken?: boolean;
}

/** Shared sandbox policy for all setup checkout preview iframes (3DS / wallets). */
export const SETUP_CHECKOUT_IFRAME_SANDBOX =
  'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation';

/**
 * Funnel checkout-node URLs are sometimes stored with an `http://` scheme. The
 * /checkout/init redirect preserves that scheme verbatim, so on an https host
 * (deployed CRM) the preview iframe redirects to an http target and the browser
 * blocks it as mixed content (net::ERR_ABORTED → the frame never fires onLoad).
 * Upgrade to https when the host page is itself https; leave http untouched on
 * local dev (http://app.localhost), where there is no mixed-content constraint
 * and the checkout host is served over http too.
 */
function upgradeCheckoutUrlScheme(checkoutUrl: string): string {
  if (typeof window === 'undefined' || window.location.protocol !== 'https:') return checkoutUrl;
  return checkoutUrl.replace(/^http:\/\//i, 'https://');
}

export function buildCheckoutInitUrl(params: BuildCheckoutInitUrlParams): string {
  const url = new URL(`${config.apiBaseUrl}/api/public/v1/checkout/init`);
  url.searchParams.set('storeId', params.storeId);
  url.searchParams.set('funnelId', params.funnelId);
  url.searchParams.set('currency', params.currency);

  if (params.checkoutUrl) {
    url.searchParams.set('checkoutUrl', upgradeCheckoutUrlScheme(params.checkoutUrl));
  }

  if (params.locale) {
    url.searchParams.set('locale', params.locale);
  }

  if (params.shippingCountryCode) {
    const alpha2 =
      normalizeSetupCountryCode(params.shippingCountryCode) ??
      resolveSetupCountryFromLocale(params.locale ?? 'en-US');
    url.searchParams.set('shippingAddress', JSON.stringify({ country: alpha2 }));
  }

  if (params.draft) {
    url.searchParams.set('draft', 'true');
  }

  if (params.includeCheckoutToken) {
    url.searchParams.set('includeCheckoutToken', 'checkoutToken');
  }

  if (params.lineItems?.length) {
    const cartItems = params.lineItems.map(({ variantId, priceId, quantity }) => ({
      variantId,
      quantity,
      ...(priceId && { priceId }),
    }));
    url.searchParams.set('items', JSON.stringify(cartItems));
  }

  return url.toString();
}

/** Shape of a funnel config node carrying the mounted checkout URL. */
type FunnelWithCheckoutNode = { config?: { nodes?: Array<{ type?: string; config?: { url?: string } }> } };

/**
 * Read the funnel's mounted checkout URL from its config. The `/checkout/init`
 * redirect needs this as the `checkoutUrl` override — without it the default
 * host resolution is broken for studio funnels and the redirect 404s.
 */
export function extractCheckoutUrlFromFunnel(funnel: unknown): string | null {
  const nodes = (funnel as FunnelWithCheckoutNode | undefined)?.config?.nodes ?? [];
  return nodes.find((node) => node?.type === 'checkout')?.config?.url ?? null;
}

export function buildSetupCheckoutPreviewUrl(params: SetupPreviewInitContext): string | null {
  if (!params.funnelId || !params.lineItem) return null;

  const { locale, shippingCountryCode } = resolveSetupPreviewInitParams(params);

  return buildCheckoutInitUrl({
    storeId: params.storeId,
    funnelId: params.funnelId,
    currency: params.currency,
    checkoutUrl: params.checkoutUrl ?? undefined,
    lineItems: [params.lineItem],
    locale,
    shippingCountryCode,
    draft: true,
  });
}
